import bilAPI from "./api";

export const getSchoolOverview = ({ params: { organization } = {}, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/analytics/school-overview/`, {
          params: { organization },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const getStandardsAnalytics = ({
  params: { organization } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/analytics/standards/`, { params: { organization }, signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Across >1 school the roster comes back empty unless `organization` narrows
// the request to one — so callers decide from the org scope, before asking,
// whether a roster is the right question at all.
export const getTeachersAnalytics = ({
  params: { organization } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/analytics/teachers/`, { params: { organization }, signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const getObservationActivity = ({
  params: { organization, days } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/analytics/observation-activity/`, {
          params: { organization, days },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const getStandardBreakdown = ({
  params: { standard, organization } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/analytics/standard-breakdown/`, {
          params: { standard, organization },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// `organization` narrows the ranking to that subtree; omitted, it spans the
// caller's whole read scope — the only way an admin staff-linked at two sibling
// schools sees both charted. So pass what the user actually PICKED, never a
// fallback: a guessed org silently shrinks the chart to one bar.
export const getSchoolRanking = ({
  params: { organization } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/analytics/school-ranking/`, { params: { organization }, signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// The head office's replacement for the teacher roster: one row per school in
// the subtree — overall score, per-standard means and roster coverage —
// O(schools × standards) instead of O(teachers).
export const getSchoolMatrix = ({
  params: { organization } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/analytics/school-matrix/`, { params: { organization }, signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Who visited whom, as names / counts / dates — the payload carries nothing
// derived from `percentage`, which is what makes a roster of colleagues
// showable at all here.
//
// `from`/`to` are this app's spelling of a date range; the endpoint calls them
// date_from/date_till and rejects one without the other.
//
// Above one school the answer is a per-school rollup in `schools` with `rows`
// EMPTY — so branch on the org scope before reading rows: an empty roster here
// means "narrow to a school first", never "nobody works here".
//
// There is no `side`. One roster answers both role groups — every row carries
// `conducted_*` and `received_*` together and `totals` counts both ends — so
// the two requests this used to take are one, and a stale `?side=` in an old
// bookmark is ignored by the endpoint rather than silently halving the answer.
export const getCoverage = ({
  params: {
    organization,
    period,
    from,
    to,
    filter,
    search,
    ordering,
    page,
    page_size,
  } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/analytics/coverage/`, {
          params: {
            organization,
            period,
            date_from: from,
            date_till: to,
            filter,
            search,
            ordering,
            page,
            page_size,
          },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};
